import { categoriesApi } from '../services/api';
import { Category } from '../types';

/**
 * Looks up category IDs for the given classification values.
 * Only returns IDs for categories that already exist in Square.
 * Does NOT create new categories - the AI should only select from existing Square categories.
 *
 * This function expects category values to match Square category names exactly
 * (as provided by enum_dynamic with source: "square_categories").
 */
export async function ensureCategoriesExist(
  classifications: {
    category?: string;
    gender?: string;
    occasion?: string;
    season?: string;
    era?: string;
    condition?: string;
  }
): Promise<string[]> {
  const categoryIds: string[] = [];

  // Get all existing categories from Square
  const existingCategoriesResponse = await categoriesApi.getAll(200);
  const existingCategories: Category[] = existingCategoriesResponse.success && existingCategoriesResponse.data
    ? existingCategoriesResponse.data
    : [];

  // Build lookup map: category name (lowercase) → category ID
  const categoryNameToId: Record<string, string> = {};
  existingCategories.forEach(cat => {
    categoryNameToId[cat.name.toLowerCase()] = cat.id;
  });

  // Look up each classification value
  for (const [, value] of Object.entries(classifications)) {
    if (!value) continue;

    const categoryNameLower = value.toLowerCase();

    // Only add ID if category exists in Square
    if (categoryNameToId[categoryNameLower]) {
      categoryIds.push(categoryNameToId[categoryNameLower]);
      console.log(`✅ Found existing category: ${value} → ${categoryNameToId[categoryNameLower]}`);
    } else {
      // Category doesn't exist - this is expected if AI returns a value not in Square
      // The AI should be configured with enum_dynamic to only select valid categories
      console.warn(`⚠️ Category not found in Square: ${value} (AI should only select from existing categories)`);
    }
  }

  return categoryIds;
}

/**
 * Gets or creates a category hierarchy (parent -> child)
 * For example: "Kimono" -> "Adult Kimono" -> "Women's Adult Kimono"
 */
export async function ensureCategoryHierarchy(
  hierarchy: string[]
): Promise<string | null> {
  if (hierarchy.length === 0) return null;
  
  // Get all existing categories
  const existingCategoriesResponse = await categoriesApi.getAll(200);
  const existingCategories: Category[] = existingCategoriesResponse.success && existingCategoriesResponse.data 
    ? existingCategoriesResponse.data 
    : [];
  
  let parentId: string | undefined = undefined;
  let lastCategoryId: string | null = null;
  
  for (const categoryName of hierarchy) {
    // Check if category exists with this parent
    const existingCategory = existingCategories.find(cat => 
      cat.name.toLowerCase() === categoryName.toLowerCase() &&
      cat.parent_category_id === parentId
    );
    
    if (existingCategory) {
      // Category exists, use it as parent for next level
      parentId = existingCategory.id;
      lastCategoryId = existingCategory.id;
    } else {
      // Create the category
      try {
        console.log(`Creating category: ${categoryName} with parent: ${parentId || 'none'}`);
        const response = await categoriesApi.create({
          name: categoryName,
          parent_category_id: parentId
        });
        
        if (response.success && response.data) {
          parentId = response.data.id;
          lastCategoryId = response.data.id;
          console.log(`✅ Created category: ${categoryName} with ID: ${response.data.id}`);
        } else {
          console.warn(`⚠️ Failed to create category: ${categoryName}`);
          break;
        }
      } catch (error) {
        console.error(`❌ Error creating category ${categoryName}:`, error);
        break;
      }
    }
  }
  
  return lastCategoryId;
}